import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Globe } from 'lucide-react';

const stats = [
  { value: '2', label: 'Active implementations' },
  { value: '5K+', label: 'Learners reached' },
];

export function HeroSection({ onOpenContact }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const play = video.play();
    if (play?.catch) play.catch(() => {});
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
      setReady(true);
    }
  }, []);

  return (
    <section
      id="hero"
      className="relative flex flex-col overflow-hidden min-h-screen px-6 md:px-10 pt-[86px] pb-7"
      style={{ background: 'linear-gradient(165deg, #2A0B02 0%, #120400 70%)' }}
      data-testid="hero-section"
    >
      {/* Media: looping video + asymmetric scrim */}
      <div className="absolute inset-0 -z-10">
        <video
          ref={videoRef}
          className={`hero-video ${ready ? 'is-ready' : ''}`}
          src="/hero-loop.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onCanPlay={() => setReady(true)}
        />
        <div className="hero-scrim" aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="flex-1 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.62fr)] gap-10 items-center lg:items-start">
        <div className="max-w-[640px] lg:self-center">
          {/* Fine print note */}
          <p
            className="rise-anim flex items-center gap-2.5 pt-3 w-fit max-w-[300px] text-[11.5px] leading-snug tracking-wide text-white/50 border-t border-white/10"
          >
            <Globe className="w-5 h-5 flex-none" />
            <span>
              Partnering with institutions<br />across emerging markets
            </span>
          </p>

          {/* Headline — Instrument Serif italic on ONE word */}
          <h1
            className="rise-anim mt-6 font-heading font-semibold leading-[0.9] tracking-[-0.035em] text-stark"
            style={{
              animationDelay: '0.06s',
              fontSize: 'clamp(2.7rem, 6.4vw, 6.1rem)',
              textShadow: '0 6px 40px rgba(0, 0, 0, 0.45)',
            }}
            data-testid="hero-headline"
          >
            Building AI<br />
            Learning Infrastructure<br />
            for the Next <em className="serif-italic">Billion</em> Users
          </h1>

          {/* Lede */}
          <p
            className="rise-anim mt-5 max-w-[440px] text-[15px] leading-[1.58] text-white/70"
            style={{ animationDelay: '0.14s' }}
          >
            Scalable AI learning systems that help businesses grow, educators teach, and underserved
            communities compete in a rapidly digitizing economy.
          </p>

          {/* CTA + social proof */}
          <div
            className="rise-anim flex items-center flex-wrap gap-5 mt-7"
            style={{ animationDelay: '0.2s' }}
          >
            <button
              onClick={onOpenContact}
              className="btn-flame"
              data-testid="hero-book-consultation"
            >
              Book Consultation
              <span className="arrow-tile" aria-hidden="true">
                <ArrowRight />
              </span>
            </button>

            <div className="flex items-center gap-2.5">
              <div className="flex" aria-hidden="true">
                <i className="avatar-dot" style={{ '--a': '#ff7a3d', '--b': '#ffb27a' }} />
                <i className="avatar-dot" style={{ '--a': '#FF3D00', '--b': '#FF8A1F' }} />
                <i className="avatar-dot" style={{ '--a': '#1f9ea8', '--b': '#63d6df' }} />
                <i className="avatar-dot" style={{ '--a': '#d8434b', '--b': '#ff8a8f' }} />
              </div>
              <span className="grid text-[10px] leading-[1.4] text-white/50">
                <strong className="text-[12px] font-medium text-white/90">
                  2 Partner Slots Open
                </strong>
                Live availability
              </span>
            </div>
          </div>

          {/* Stat cards */}
          <ul
            className="rise-anim flex flex-wrap gap-3.5 mt-8 p-0 list-none"
            style={{ animationDelay: '0.28s' }}
          >
            {stats.map((stat, i) => (
              <li
                key={stat.label}
                className={`ember-stat w-[200px] ${i === 1 ? 'alt' : ''}`}
                data-testid={`hero-stat-${i}`}
              >
                <span className="absolute top-3.5 right-4 text-[15px] text-white/50">*</span>
                <span
                  className="font-heading font-medium leading-none tracking-[-0.03em] text-stark"
                  style={{ fontSize: 'clamp(1.7rem, 2.4vw, 2.3rem)' }}
                >
                  {stat.value}
                </span>
                <span className="text-[11.5px] text-white/50">{stat.label}</span>
                <span className="absolute right-4 bottom-5 w-3.5 h-px bg-white/30" aria-hidden="true" />
              </li>
            ))}
          </ul>
        </div>

        {/* Ghost analytics panel — appears only ≥1120px */}
        <aside
          className="hidden xl:block self-start mt-10 max-w-[330px] justify-self-end text-white/25"
          aria-hidden="true"
        >
          <div className="flex items-end gap-4">
            <div className="flex items-end gap-1.5 h-[74px]">
              {[34, 52, 44, 70, 88].map((h, i) => (
                <span key={i} className="w-2 rounded-t-sm bg-white/40" style={{ height: `${h}%` }} />
              ))}
            </div>
            <p className="text-[11px] leading-[1.4]">
              <strong className="block font-heading text-[22px] font-semibold tracking-[-0.03em] text-white/55">
                +42%
              </strong>
              Learning<br />Outcomes
            </p>
          </div>
          <h2 className="mt-7 font-heading text-[22px] font-medium tracking-[-0.02em] text-white/40">
            Measure Real Impact
          </h2>
          <p className="mt-2 text-[12.5px] leading-[1.6]">
            We track every learner cohort through meaningful metrics and refine every detail until
            the system feels effortless.
          </p>
        </aside>
      </div>

      {/* Footer band — "SVN" watermark + focus tags */}
      <div
        className="rise-anim relative flex items-end justify-between gap-6 mt-6"
        style={{ animationDelay: '0.36s' }}
      >
        <span
          className="font-heading font-bold leading-[0.8] tracking-[-0.05em] text-white/5 select-none"
          style={{ fontSize: 'clamp(3rem, 8vw, 7rem)' }}
          aria-hidden="true"
        >
          SVN
        </span>
        <div className="text-right hidden md:block">
          <span className="block mb-3 text-[11.5px] text-white/50">Focus Areas</span>
          <ul className="flex items-center gap-5 justify-end m-0 p-0 list-none flex-wrap">
            {['AI Infrastructure', 'EdTech', 'Emerging Markets', 'Automation'].map((t) => (
              <li
                key={t}
                className="inline-flex items-center text-[14px] font-normal tracking-[-0.01em] text-white/85"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
